/**
 * Cloudflare R2 Upload Utility - Admin Panel
 * 
 * Firebase Storage yerine Cloudflare R2 Worker üzerinden
 * dosya yükleme, silme ve URL oluşturma işlemlerini yönetir.
 * 
 * Android tarafındaki CloudflareR2Storage.kt ile aynı token mekanizmasını kullanır.
 */

import { sha256 } from 'js-sha256';

const R2_WORKER_URL = 'https://cdn.technolives.com';
const UPLOAD_SECRET = 'bimbo.cdn.2026!';
const TOKEN_EXPIRY_MS = 600_000; // 10 dakika

function toBase64Url(bytes) {
  const base64 = btoa(String.fromCharCode(...new Uint8Array(bytes)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * HMAC-SHA256 tabanlı upload token oluştur
 * Android'deki generateToken() ile birebir aynı mantık
 *
 * WebCrypto (crypto.subtle) yalnızca güvenli bağlamlarda (HTTPS/localhost)
 * tanımlıdır. Panel düz HTTP üzerinden açıldığında saf JS uygulamasına düşeriz.
 */
async function generateToken(path) {
  const expiry = Date.now() + TOKEN_EXPIRY_MS;
  const message = `${path}:${expiry}`;

  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;

  if (subtle) {
    const encoder = new TextEncoder();
    const key = await subtle.importKey(
      'raw',
      encoder.encode(UPLOAD_SECRET),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await subtle.sign('HMAC', key, encoder.encode(message));
    return `${toBase64Url(signature)}.${expiry}`;
  }

  const signature = sha256.hmac.arrayBuffer(UPLOAD_SECRET, message);
  return `${toBase64Url(signature)}.${expiry}`;
}

function sanitizeR2Path(path) {
  const parts = String(path || '').replace(/^\/+/, '').split('/').filter(Boolean);
  if (!parts.length) return String(path || '');
  const lastIdx = parts.length - 1;
  const last = parts[lastIdx];
  if (/[^A-Za-z0-9._-]/.test(last)) {
    const ext = last.includes('.')
      ? last.split('.').pop().toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin'
      : 'bin';
    parts[lastIdx] = `${Date.now()}_${generateUUID()}.${ext}`;
  }
  return parts.join('/');
}

function encodeR2Path(path) {
  return String(path).split('/').map(encodeURIComponent).join('/');
}

/**
 * Dosyayı R2'ye yükle
 * @param {File} file - Yüklenecek dosya (File nesnesi)
 * @param {string} path - R2'deki hedef yol (örn: "profile_images/uid123/photo.jpg")
 * @param {function} onProgress - İlerleme callback'i (0-100) (opsiyonel)
 * @returns {Promise<string>} Yüklenen dosyanın public URL'si
 */
export async function uploadToR2(file, path, onProgress = null) {
  path = sanitizeR2Path(path);
  const token = await generateToken(path);
  const uploadUrl = `${R2_WORKER_URL}/${encodeR2Path(path)}?token=${encodeURIComponent(token)}`;

  console.log(`[R2] Uploading: ${path} (${(file.size / 1024).toFixed(1)} KB)`);

  // XMLHttpRequest ile progress tracking (opsiyonel)
  if (onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const publicUrl = `${R2_WORKER_URL}/${encodeR2Path(path)}`;
          console.log(`[R2] Upload success: ${publicUrl}`);
          resolve(publicUrl);
        } else {
          reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.send(file);
    });
  }

  // Basit fetch ile upload (progress yok)
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed (${response.status}): ${errorText}`);
  }

  const publicUrl = `${R2_WORKER_URL}/${encodeR2Path(path)}`;
  console.log(`[R2] Upload success: ${publicUrl}`);
  return publicUrl;
}

/**
 * Blob/ArrayBuffer'dan R2'ye yükle
 * @param {Blob|ArrayBuffer} data - Yüklenecek veri
 * @param {string} path - R2'deki hedef yol
 * @param {string} contentType - MIME tipi
 * @returns {Promise<string>} Public URL
 */
export async function uploadBlobToR2(data, path, contentType = 'application/octet-stream') {
  path = sanitizeR2Path(path);
  const token = await generateToken(path);
  const uploadUrl = `${R2_WORKER_URL}/${encodeR2Path(path)}?token=${encodeURIComponent(token)}`;

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: data,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed (${response.status}): ${errorText}`);
  }

  return `${R2_WORKER_URL}/${encodeR2Path(path)}`;
}

/**
 * R2'den dosya sil
 * @param {string} path - Silinecek dosyanın R2 yolu
 * @returns {Promise<boolean>} Başarılı mı
 */
export async function deleteFromR2(path) {
  path = String(path || '').replace(/^\/+/, '');
  const token = await generateToken(path);
  const deleteUrl = `${R2_WORKER_URL}/${encodeR2Path(path)}?token=${encodeURIComponent(token)}`;

  try {
    const response = await fetch(deleteUrl, { method: 'DELETE' });
    return response.ok;
  } catch (e) {
    console.error(`[R2] Delete failed: ${e.message}`);
    return false;
  }
}

/**
 * Public URL oluştur (indirme/görüntüleme için)
 */
export function getR2PublicUrl(path) {
  return `${R2_WORKER_URL}/${encodeR2Path(path)}`;
}

/** Tarayıcıda video/ses oynatmak için path segmentlerini encode eder. */
export function toPlayableMediaUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const parsed = new URL(url);
    parsed.pathname = parsed.pathname
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        try {
          return encodeURIComponent(decodeURIComponent(segment));
        } catch (_) {
          return encodeURIComponent(segment);
        }
      })
      .join('/');
    return parsed.toString();
  } catch (_) {
    return url;
  }
}

/**
 * UUID oluştur (dosya adı için)
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Android OkHttp köşeli parantez/boşluk içeren adları bozar; sadece uuid.uzantı kullan. */
export function sanitizeStorageFileName(originalName = '') {
  const ext = String(originalName).split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
  return `${generateUUID()}.${ext}`;
}

export const R2_WORKER_BASE_URL = R2_WORKER_URL;
